import { createContext, useContext, useCallback, useState, useEffect, useRef, type ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import { useInboxChannel } from '@/hooks/useCable'
import { playNotificationSound, type SoundPreference } from '@/lib/sounds'
import type { InboxMessage } from '@/lib/cable'

interface Notification {
  id: string
  type: 'new_conversation' | 'new_message'
  conversationId: string
  timestamp: number
}

interface NotificationsContextValue {
  unreadCount: number
  notifications: Notification[]
  markAsRead: (conversationId: string) => void
  clearAll: () => void
  subscribe: (callback: (data: InboxMessage) => void) => () => void
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

interface SharedProps {
  current_account?: { slug: string }
  current_account_user?: { sound_preference: SoundPreference }
}

interface NotificationsProviderProps {
  children: ReactNode
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const pageProps = usePage().props as unknown as SharedProps
  const accountSlug = pageProps.current_account?.slug
  const soundPreference = pageProps.current_account_user?.sound_preference || 'chirp'

  const [unreadCount, setUnreadCount] = useState(0)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const subscribersRef = useRef<Set<(data: InboxMessage) => void>>(new Set())
  const originalTitleRef = useRef<string>('')

  // Store original page title on mount
  useEffect(() => {
    originalTitleRef.current = document.title.replace(/^\(\d+\)\s*/, '')
  }, [])

  // Update page title when unread count changes
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `👻 ${originalTitleRef.current}`
    } else {
      document.title = originalTitleRef.current
    }
  }, [unreadCount])

  // Handle incoming WebSocket messages
  const handleCableMessage = useCallback((data: InboxMessage) => {
    if (data.type === 'new_conversation' || data.type === 'conversation_updated') {
      // Only play sound for messages FROM visitors (not agent messages)
      const isFromVisitor = data.conversation.last_message?.sender_type === 'Visitor'
      if (isFromVisitor) {
        playNotificationSound(soundPreference)
      }

      // Add to notifications (only for visitor messages)
      if (isFromVisitor) {
        const notification: Notification = {
          id: `${data.conversation.id}-${Date.now()}`,
          type: data.type === 'new_conversation' ? 'new_conversation' : 'new_message',
          conversationId: data.conversation.id,
          timestamp: Date.now(),
        }

        setNotifications(prev => [notification, ...prev].slice(0, 50)) // Keep last 50
        setUnreadCount(prev => prev + 1)
      }
    }

    // Notify all subscribers
    subscribersRef.current.forEach(callback => callback(data))
  }, [soundPreference])

  // Subscribe to inbox channel
  useInboxChannel(accountSlug, handleCableMessage)

  // Subscribe function for components to receive raw cable messages
  const subscribe = useCallback((callback: (data: InboxMessage) => void) => {
    subscribersRef.current.add(callback)
    return () => {
      subscribersRef.current.delete(callback)
    }
  }, [])

  const markAsRead = useCallback((conversationId: string) => {
    setNotifications(prev => prev.filter(n => n.conversationId !== conversationId))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }, [])

  const clearAll = useCallback(() => {
    setNotifications([])
    setUnreadCount(0)
  }, [])

  return (
    <NotificationsContext.Provider value={{
      unreadCount,
      notifications,
      markAsRead,
      clearAll,
      subscribe,
    }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}

/**
 * Hook for components to subscribe to raw cable messages
 * Use this when you need to handle the full message data (e.g., updating conversation list)
 * Safe to call outside NotificationsProvider - will be a no-op
 */
export function useNotificationSubscription(callback: (data: InboxMessage) => void) {
  const context = useContext(NotificationsContext)

  useEffect(() => {
    if (!context) return
    return context.subscribe(callback)
  }, [context, callback])
}
